import { FC, useEffect, useState } from 'react';
import { SecondaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { Spinner } from '@monorepo/base/src/components/spinner/spinner';
import { IModalInjectProps } from '@monorepo/controlled/src/components/modal/modal/modal';
import { isAbortError } from '@monorepo/tools/src/lib/tools/ask/guards';
import { IAskError } from '@monorepo/tools/src/lib/tools/ask/ask';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { ICampaignAvailabilityCampaign, NotificationsPageApi } from '../../apis/notifications-page.api';
import styles from './notifications-center-page.module.scss';

interface IPausedCampaignsModalProps extends Partial<IModalInjectProps> {
	accountId?: id | null;
}

export const PausedCampaignsModal: FC<IPausedCampaignsModalProps> = ({ closeModal, accountId }) => {
	const [campaigns, setCampaigns] = useState<ICampaignAvailabilityCampaign[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		setIsLoading(true);
		setError(null);
		NotificationsPageApi.fetchCampaignAvailabilityAffectedCampaigns(accountId)
			.then(setCampaigns)
			.catch((err: unknown) => {
				if (isAbortError(err as IAskError)) {
					return;
				}
				setError('Could not load paused campaigns. Please try again.');
			})
			.finally(() => setIsLoading(false));
	}, [accountId]);

	return (
		<div className={styles.modal}>
			<div className={styles.modalHeader}>
				<h3 className={styles.modalTitle}>Paused campaigns</h3>
				<p className={styles.modalDescription}>
					These campaigns had recent activity from your traffic but are not currently active.
				</p>
			</div>

			{error ? <div className={styles.error}>{error}</div> : null}
			{isLoading ? (
				<div className={styles.loadingState}>
					<Spinner size="18px" />
					<p className={styles.stateText}>Loading campaigns...</p>
				</div>
			) : null}
			{!isLoading && !error && campaigns.length === 0 ? <p className={styles.stateText}>No paused campaigns found.</p> : null}
			{!isLoading && !error && campaigns.length > 0 ? (
				<div className={styles.campaignList}>
					{campaigns.map(campaign => (
						<div className={styles.campaignRow} key={campaign.id}>
							<span className={styles.campaignName}>{campaign.name}</span>
							<span className={styles.campaignId}>Campaign ID: {campaign.id}</span>
						</div>
					))}
				</div>
			) : null}

			<div className={styles.modalFooter}>
				<SecondaryButton onClick={closeModal}>Close</SecondaryButton>
			</div>
		</div>
	);
};
